import { useEffect, useRef } from 'react';
import { PlatformSection } from '@/common/components';
import { usePlatform } from '@/common/hooks';
import { useNewTranslations } from '@/common/translations';
import type { PlayerOnPitchStatsType } from '@/common/types';
import { PlayerOnPitch, HorizontalScrollView } from '@/ui/components';

import { usePrematchData } from '../../hooks';
import { TeamFilterOptionValues } from '../../constants';
import type { TeamFilterOption } from '../../types';

interface SubstitutesSectionProps {
  activePlayerStatsFilter: PlayerOnPitchStatsType | null;
  activeTeamFilter: TeamFilterOption;
  jerseySrc: string;
}

export default function SubstitutesSection(props: SubstitutesSectionProps) {
  const { activePlayerStatsFilter, activeTeamFilter, jerseySrc } = props;

  const { lineup } = usePrematchData();
  const platform = usePlatform();

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const translate = useNewTranslations();

  useEffect(() => {
    scrollRef.current?.scrollTo({ left: 0, behavior: 'auto' });
  }, [activeTeamFilter]);

  if (!lineup) {
    return null;
  }

  const team = activeTeamFilter === TeamFilterOptionValues.HOME_TEAM ? lineup.home : lineup.away;

  return (
    <PlatformSection>
      <div className="relative flex justify-center flex-col gap-4">
        <span className="font-robotoMedium font-medium text-[12px] leading-[16px] text-pmw-label-primary-dark">
          {translate('PreMatchStats.Substitutes')}
        </span>
        {/*
          overflow-y-hidden is added just in case because sometimes the area is
          scrollable vertically even though it shouldn't
          see https://bitbucket.org/suzu_kindred/stats_widgets/pull-requests/209#comment-781791960
         */}
        <HorizontalScrollView
          ref={scrollRef}
          className="mx-[-8px] px-[8px] flex"
          platform={platform}
        >
          {team.bench.map(({ name, goals, assists }) => (
            <PlayerOnPitch
              key={`${name}-${goals}-${assists}`}
              jerseySrc={jerseySrc}
              playerName={name}
              goals={goals}
              assists={assists}
              activeStat={activePlayerStatsFilter ?? undefined}
            />
          ))}
        </HorizontalScrollView>
      </div>
    </PlatformSection>
  );
}
